import React from 'react';
import { Info, DollarSign, Calculator, PiggyBank, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о кредитах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о кредитах: условия, типы, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Что такое кредит?
        </h3>
        <p>
          <strong>Кредит</strong> — это финансовая услуга, при которой банк или кредитная организация (кредитор) предоставляет заемщику денежные средства на определенных условиях, включая срок возврата, процентную ставку и график платежей. Заемщик обязуется вернуть полученную сумму и выплатить проценты за пользование деньгами.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Ключевые параметры кредита
        </h3>
        <ul>
          <li><strong>Сумма кредита:</strong> Размер заемных средств, которые выдаются заемщику.</li>
          <li><strong>Процентная ставка:</strong> Плата за пользование кредитными средствами, указывается в процентах годовых.</li>
          <li><strong>Срок кредита:</strong> Период, на который выдается кредит (от нескольких месяцев до нескольких лет).</li>
          <li>
            <strong>Тип платежа:</strong>
            <ul>
              <li><strong>Аннуитетный:</strong> Равные ежемесячные платежи. В начале выплат большую часть составляют проценты, к концу — основной долг.</li>
              <li><strong>Дифференцированный:</strong> Платежи уменьшаются к концу срока. Основной долг гасится равными долями, проценты начисляются на остаток. Первые платежи выше, но общая переплата меньше.</li>
            </ul>
          </li>
          <li><strong>Полная стоимость кредита (ПСК):</strong> Включает все платежи по кредиту, выраженные в годовых процентах.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Основные типы кредитов
        </h3>
        <ul>
          <li><strong>Потребительские кредиты:</strong> На любые личные нужды без обеспечения. Ставка обычно выше из-за повышенного риска для банка.</li>
          <li><strong>Целевые кредиты:</strong> На конкретные цели (например, автокредит, образовательный кредит). Обычно имеют более низкие ставки.</li>
          <li><strong>Кредитные карты:</strong> Возобновляемая кредитная линия с льготным периодом.</li>
          <li><strong>Рефинансирование:</strong> Получение нового кредита для погашения существующего на более выгодных условиях.</li>
          <li><strong>Ипотека:</strong> Долгосрочный кредит на покупку недвижимости под ее залог.</li>
          <li><strong>Бизнес-кредиты:</strong> Для предпринимателей и организаций на развитие бизнеса.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Как правильно оценить свои возможности?</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Оптимальная долговая нагрузка — не более 30-40% от вашего ежемесячного дохода. При расчете учитывайте все имеющиеся кредиты и займы.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Досрочное погашение кредита
        </h3>
        <p>
          Досрочное погашение — внесение суммы сверх ежемесячного платежа. Это позволяет сократить срок кредита или уменьшить ежемесячный платеж, а также значительно снизить переплату по процентам.
        </p>
        <ul>
          <li>
            <strong>Уменьшение срока:</strong> Размер платежа остается прежним, но срок кредита сокращается. Это дает большую экономию на процентах.
          </li>
          <li>
            <strong>Уменьшение платежа:</strong> Срок остается прежним, но снижается ежемесячная нагрузка. Подходит, если важно снизить текущие расходы.
          </li>
        </ul>
        <p>
          Большинство банков не взимают комиссию за досрочное погашение, но могут устанавливать минимальную сумму и требовать предварительного уведомления.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при оформлении кредита
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно читайте договор, особенно мелкий шрифт</li>
            <li>Уточняйте полную стоимость кредита с учетом всех комиссий и дополнительных услуг</li>
            <li>Проверьте наличие и размер штрафов за просрочку платежей</li>
            <li>Узнайте условия досрочного погашения</li>
            <li>Обращайте внимание на дополнительные услуги (страховки и т.д.): насколько они обязательны</li>
          </ul>
        </div>

        <h3>Как улучшить условия по кредиту?</h3>
        <ul>
          <li>Поддерживайте положительную кредитную историю</li>
          <li>Предоставьте обеспечение или привлеките созаемщика</li>
          <li>Рассмотрите зарплатные проекты банков (обычно дают льготные ставки)</li>
          <li>Сравнивайте предложения разных банков перед оформлением</li>
          <li>Следите за акциями и специальными предложениями банков</li>
          <li>Рассмотрите рефинансирование, если общая ставка снизилась</li>
        </ul>

        <h3>Что делать при финансовых трудностях?</h3>
        <p>
          Если вы столкнулись с трудностями в выплате кредита:
        </p>
        <ol>
          <li><strong>Не избегайте контакта с банком</strong> — чем раньше вы сообщите о проблеме, тем больше вариантов решения</li>
          <li><strong>Запросите реструктуризацию</strong> — изменение условий договора (продление срока, кредитные каникулы)</li>
          <li><strong>Рассмотрите рефинансирование</strong> в другом банке на более комфортных условиях</li>
          <li><strong>Проконсультируйтесь с юристом</strong> о законных способах решения ситуации</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш кредитный калькулятор поможет вам спланировать бюджет, оценить свои возможности и выбрать оптимальные условия кредита!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;